import { template } from "@ember/template-compiler";
import Component from '@glimmer/component';
// Resets import must come before sprinkle imports, to ensure reset styles have
// lower specificity
import { resetStyles } from '@/css';
import { sprinkles, type Sprinkles } from '@/css/sprinkles.css';
import clsx from 'clsx';
import type { ElementSignature, ElementFromTagName } from 'ember-element-helper';
import { element } from 'ember-element-helper';
import type { ComponentLike } from '@glint/template';
import { backgroundColor as backgroundColorStyles } from '@/css/background.css';
import { defaultColorOverride } from '@/css/typography.css';
export interface BoxSignature<T extends string = 'div'> {
    Element: ElementFromTagName<T>;
    Args: {
        as?: T | ElementSignature<T>['Return'] | ComponentLike;
        backgroundColor?: keyof typeof backgroundColorStyles;
    } & Sprinkles;
    Blocks: {
        default: [];
    };
}
export default class Box<T extends string = 'div'> extends Component<BoxSignature<T>> {
    get as() {
        return (this.args.as ?? 'div') as any;
    }
    get classNames() {
        const { as: as1, backgroundColor: backgroundColor1, ...restArgs1 } = this.args;
        const sprinkleProps1: Record<string, unknown> = {};
        for(const key1 in restArgs1){
            if (sprinkles.properties.has(key1 as keyof Sprinkles)) {
                sprinkleProps1[key1] = this.args[key1 as keyof typeof this.args];
            }
        }
        // TODO: Seeing a bit of repetition between Ember and React components -
        // move shared logic into a module to be imported by both framework
        // components.
        let textColorClassName1: string | null = null;
        if (backgroundColor1 != null && backgroundColor1 !== 'base') {
            textColorClassName1 = defaultColorOverride[backgroundColor1];
        }
        const classNames1 = clsx(resetStyles.base, // Applies element-type based reset style, if it exists
        typeof as1 === 'string' ? resetStyles.element[as1 as keyof typeof resetStyles.element] : null, backgroundColor1 != null ? backgroundColorStyles[backgroundColor1] : null, textColorClassName1, sprinkles(sprinkleProps1));
        return classNames1;
    }
    get isComponentClass() {
        return typeof this.as === 'object' || typeof this.as === 'function';
    }
    static{
        template(`
		{{~#let (if this.isComponentClass this.as (element this.as)) as |Element|~}}
			<Element
				class={{this.classNames}}
				{{! @glint-expect-error }}
				...attributes
			>
				{{~yield~}}
			</Element>
		{{~/let~}}
	`, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
